import { useMemo, useState } from 'react'
import {
  Box, IconButton, InputAdornment, Popover, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import AllInclusiveRoundedIcon from '@mui/icons-material/AllInclusiveRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { Customer, CustomerGroupTreeNode } from '@cmms/api-client'

import { api } from '../lib/api'
import { useCustomerStore } from '../store/customerStore'
import { ORG_NODE_STYLE } from '../lib/orgStructureTree'

interface Props {
  collapsed: boolean
}

// ─── Merged display tree ────────────────────────────────────────────────────
//
// Groups/Sub Groups come from customer-groups.tree(); customers come from
// customers.list() (already carrying group_id). Merged client-side into one
// tree so the picker reads as a single hierarchy: Group -> Sub Group ->
// Customer, with ungrouped customers as their own top-level leaves —
// customer_groups.tree() only knows about Groups/Sub Groups, it has no idea
// customers exist at all.

type DisplayNode =
  | { kind: 'group' | 'subgroup'; id: string; name: string; children: DisplayNode[] }
  | { kind: 'customer'; id: string; name: string; siteCount?: number; children: [] }

function buildDisplayTree(groups: CustomerGroupTreeNode[], customers: Customer[]): DisplayNode[] {
  const byGroupId = new Map<string, Customer[]>()
  for (const c of customers) {
    if (!c.group_id) continue
    const list = byGroupId.get(c.group_id) ?? []
    list.push(c)
    byGroupId.set(c.group_id, list)
  }

  function convert(node: CustomerGroupTreeNode): DisplayNode {
    const isSubGroup = Boolean(node.parent_id)
    const customerLeaves: DisplayNode[] = isSubGroup
      ? (byGroupId.get(node.id) ?? []).map((c) => ({
          kind: 'customer' as const, id: c.id, name: c.name, siteCount: c.site_count, children: [],
        }))
      : []
    return {
      kind: isSubGroup ? 'subgroup' : 'group',
      id: node.id,
      name: node.name,
      children: [...node.children.map(convert), ...customerLeaves],
    }
  }

  const groupNodes = groups.map(convert)
  const ungrouped: DisplayNode[] = customers
    .filter((c) => !c.group_id)
    .map((c) => ({ kind: 'customer' as const, id: c.id, name: c.name, siteCount: c.site_count, children: [] }))

  return [...groupNodes, ...ungrouped]
}

/// Active-customer / active-group picker for the sidebar. Shows the
/// current selection ("All", a Group/Sub Group, or a specific
/// customer), opens a tree of every Group/Sub Group/Customer the
/// caller has access to, and on change:
///   1. Writes the choice into the customerStore (which the api-client
///      reads on every subsequent request, as either X-Customer-Id or
///      X-Scope-Kind/X-Scope-Id — the two are mutually exclusive).
///   2. Invalidates the entire query cache so every list refetches
///      with the new scope.
///
/// Hidden entirely for users who only have access to one customer and
/// no groups exist — the switcher is just noise in that case.
export function CustomerSwitcher({ collapsed }: Props) {
  const qc = useQueryClient()
  const customerId = useCustomerStore((s) => s.customerId)
  const groupScope = useCustomerStore((s) => s.groupScope)
  const setCustomer = useCustomerStore((s) => s.setCustomer)
  const setGroupScope = useCustomerStore((s) => s.setGroupScope)
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [query, setQuery] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const open = Boolean(anchor)

  const customersQ = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.customers.list(),
    staleTime: 5 * 60_000,
  })
  const customers = useMemo(() => Array.isArray(customersQ.data) ? customersQ.data : [], [customersQ.data])

  // customer_groups:read is broad (org_admin/customer_admin/technician/
  // viewer all get it) but not universal — a 403 here just means "no
  // groups exist for this caller," fall back to the flat customer list
  // exactly like before this feature existed.
  const groupsQ = useQuery({
    queryKey: ['customer-groups-tree'],
    queryFn: () => api.customerGroups.tree(),
    staleTime: 60_000,
    retry: false,
  })
  const groups = useMemo(() => Array.isArray(groupsQ.data) ? groupsQ.data : [], [groupsQ.data])

  const tree = useMemo(() => buildDisplayTree(groups, customers), [groups, customers])
  const q = query.trim().toLowerCase()
  const matchIds = useMemoMatch(tree, q)

  // Single-customer accounts with no groups don't need a switcher —
  // keeps the sidebar uncluttered for the most common case. Hook calls
  // above this point unconditionally so the early return doesn't skip
  // any of them on a later render.
  if (customersQ.isSuccess && customers.length <= 1 && groups.length === 0) return null
  if (customersQ.isLoading) return null

  const selectedCustomer = customerId ? customers.find((c) => c.id === customerId) : null
  const label = groupScope?.name ?? selectedCustomer?.name ?? 'All customers'
  // Same icon/color per kind as the Org Structure tree in Settings, so
  // a Group/Sub Group/Customer selection reads the same way here as it
  // does there. `null` means "All customers" — no kind to color.
  const headerStyle = groupScope
    ? ORG_NODE_STYLE[groupScope.kind]
    : selectedCustomer
      ? ORG_NODE_STYLE.customer
      : null

  function closeMenu() {
    setAnchor(null)
    setQuery('')
  }

  function afterChange() {
    closeMenu()
    // Drop every cached query so the next render fires fresh requests
    // with the new scope headers. Cheaper than tracking which queries
    // are customer/group-scoped and which aren't.
    qc.invalidateQueries()
  }

  function selectAll() {
    setCustomer(null)
    afterChange()
  }
  function selectNode(node: DisplayNode) {
    if (node.kind === 'customer') setCustomer(node.id)
    else setGroupScope({ kind: node.kind, id: node.id, name: node.name })
    afterChange()
  }
  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const activeId = groupScope?.id ?? customerId ?? null

  return (
    <Box sx={{
      px: collapsed ? 0 : 2.5, pb: 1.25,
      ...(collapsed && { display: 'flex', justifyContent: 'center' }),
    }}>
      {collapsed ? (
        <Tooltip title={`Scope: ${label}`} placement="right" arrow>
          <IconButton
            size="small"
            onClick={(e) => setAnchor(e.currentTarget)}
            sx={{ color: 'text.secondary', width: 32, height: 32, borderRadius: 1.5, '&:hover': { bgcolor: 'action.hover' } }}
          >
            <BusinessOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      ) : (
        <Box
          onClick={(e) => setAnchor(e.currentTarget)}
          role="button"
          aria-label="Switch customer or group"
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
            borderRadius: 1.5, cursor: 'pointer',
            border: '1px solid', borderColor: 'divider',
            bgcolor: 'background.paper',
            transition: 'border-color 120ms ease, background-color 120ms ease',
            '&:hover': { borderColor: 'primary.main' },
          }}
        >
          <Box sx={{
            width: 26, height: 26, borderRadius: 1, display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center',
            bgcolor: headerStyle ? `${headerStyle.color}1A` : (t) => `${t.palette.primary.main}14`,
            color: headerStyle?.color ?? 'primary.main',
            flexShrink: 0,
          }}>
            {headerStyle
              ? <headerStyle.icon sx={{ fontSize: 16 }} />
              : <AllInclusiveRoundedIcon sx={{ fontSize: 16 }} />}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 10.5, color: 'text.secondary', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {groupScope?.kind === 'group' ? 'Group' : groupScope?.kind === 'subgroup' ? 'Sub Group' : 'Customer'}
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em' }} noWrap>
              {label}
            </Typography>
          </Box>
          <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        </Box>
      )}

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: {
          mt: 0.5, width: anchor?.offsetWidth ?? 300, minWidth: 280, maxHeight: 420,
          borderRadius: 2.5, display: 'flex', flexDirection: 'column', overflow: 'hidden',
        } } }}
      >
        {(customers.length > 6 || groups.length > 0) && (
          <Box sx={{ px: 1.5, pt: 1.25, pb: 1.25, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
            <TextField
              autoFocus fullWidth size="small"
              placeholder="Search customers, groups…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key !== 'Escape') e.stopPropagation() }}
              slotProps={{ input: { startAdornment: (
                <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} /></InputAdornment>
              ) } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 999, fontSize: 13, bgcolor: 'action.hover',
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&.Mui-focused': { bgcolor: 'background.paper' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: '1.5px solid', borderColor: 'primary.main' },
                },
                '& .MuiInputBase-input': { py: 0.85 },
              }}
            />
          </Box>
        )}

        <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
          {q.length === 0 && (
            <Box
              onClick={selectAll}
              role="button"
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.25,
                px: 1.75, py: 1, mx: 0.5, mb: 0.5, borderRadius: 1.5, cursor: 'pointer',
                borderBottom: '1px solid', borderColor: 'divider',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <AllInclusiveRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: 600 }}>All customers</Typography>
              {activeId === null && <CheckRoundedIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
            </Box>
          )}

          {matchIds && matchIds.hits.size === 0 && (
            <Box sx={{ px: 2, py: 2.5 }}>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', textAlign: 'center' }}>
                No matches for &ldquo;{query}&rdquo;
              </Typography>
            </Box>
          )}
          {tree.map((node) => (
            <SwitcherTreeNode
              key={node.id} node={node} depth={0} activeId={activeId}
              expanded={expanded} onToggle={toggle} onSelect={selectNode} matchIds={matchIds}
            />
          ))}
        </Box>
      </Popover>
    </Box>
  )
}

/// Search: which node ids match, and which ids (matches + their
/// ancestors) should stay visible while a query is active.
function useMemoMatch(tree: DisplayNode[], q: string) {
  return useMemo(() => {
    if (!q) return null
    const hits = new Set<string>()
    function walk(nodes: DisplayNode[]) {
      for (const n of nodes) {
        if (n.name.toLowerCase().includes(q)) hits.add(n.id)
        walk(n.children)
      }
    }
    walk(tree)
    const visible = new Set<string>()
    function mark(nodes: DisplayNode[], ancestors: string[]): boolean {
      let any = false
      for (const n of nodes) {
        const childHit = mark(n.children, [...ancestors, n.id])
        if (hits.has(n.id) || childHit) {
          visible.add(n.id)
          ancestors.forEach((a) => visible.add(a))
          any = true
        }
      }
      return any
    }
    mark(tree, [])
    return { hits, visible }
  }, [tree, q])
}

// ─── Tree node ───────────────────────────────────────────────────────────────

function SwitcherTreeNode({ node, depth, activeId, expanded, onToggle, onSelect, matchIds }: {
  node: DisplayNode; depth: number; activeId: string | null
  expanded: Set<string>; onToggle: (id: string) => void
  onSelect: (node: DisplayNode) => void
  matchIds: { hits: Set<string>; visible: Set<string> } | null
}) {
  if (matchIds && !matchIds.visible.has(node.id)) return null
  const hasChildren = node.children.length > 0
  const isExpanded = expanded.has(node.id) || Boolean(matchIds)
  const isSelected = node.id === activeId
  const { icon: Icon, color } = ORG_NODE_STYLE[node.kind]

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1}
        onClick={() => onSelect(node)}
        sx={{
          pl: 1.25 + depth * 2.25, pr: 1.5, py: 0.85, cursor: 'pointer',
          bgcolor: isSelected ? (t) => `${t.palette.primary.main}14` : 'transparent',
          '&:hover': { bgcolor: isSelected
            ? (t) => `${t.palette.primary.main}20`
            : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)' },
        }}
      >
        <Box
          onClick={(e) => { e.stopPropagation(); if (hasChildren) onToggle(node.id) }}
          sx={{ width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: 'text.secondary', flexShrink: 0, opacity: hasChildren ? 1 : 0, cursor: hasChildren ? 'pointer' : 'default',
            transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 150ms ease' }}>
          <ChevronRightIcon sx={{ fontSize: 15 }} />
        </Box>
        <Box sx={{
          width: 22, height: 22, borderRadius: 1, flexShrink: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: `${color}1A`, color,
        }}>
          <Icon sx={{ fontSize: 14 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: isSelected ? 700 : 600 }} noWrap>{node.name}</Typography>
          {node.kind === 'customer' && typeof node.siteCount === 'number' && (
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
              {node.siteCount} site{node.siteCount === 1 ? '' : 's'}
            </Typography>
          )}
        </Box>
        {isSelected && <CheckRoundedIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
      </Stack>

      {hasChildren && isExpanded && node.children.map((child) => (
        <SwitcherTreeNode
          key={child.id} node={child} depth={depth + 1} activeId={activeId}
          expanded={expanded} onToggle={onToggle} onSelect={onSelect} matchIds={matchIds}
        />
      ))}
    </Box>
  )
}
